/**
 * Trunnion AI - Breadcrumb Navigation
 * Context-aware breadcrumb bar that parses the current URL into navigable segments.
 * Placed below the TopNav / above the page content area.
 */

import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useBreadcrumbs } from '@/hooks/useBreadcrumbs';
import { ROUTES } from '@/config/routes';

export function Breadcrumbs() {
  const { segments } = useBreadcrumbs();

  // Don't render breadcrumbs on the dashboard (root) or when there are no segments
  if (segments.length === 0) {
    return null;
  }

  // Check if the only segment is 'dashboard' -- no breadcrumbs needed
  if (segments.length === 1 && segments[0].path === ROUTES.DASHBOARD) {
    return null;
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-1.5 text-sm px-6 py-2 bg-trunnion-slate-900/50 border-b border-trunnion-slate-800"
    >
      {/* Home icon -- always links to Dashboard */}
      <Link
        to={ROUTES.DASHBOARD}
        className="flex items-center text-trunnion-slate-400 hover:text-trunnion-gold-500 transition-colors"
        aria-label="Dashboard"
      >
        <Home className="h-3.5 w-3.5" />
      </Link>

      {/* Segments */}
      {segments.map((segment) => {
        // Skip rendering the 'dashboard' segment since it's represented by the Home icon
        if (segment.path === ROUTES.DASHBOARD) {
          return null;
        }

        return (
          <span key={segment.path} className="flex items-center gap-1.5">
            {/* Separator */}
            <ChevronRight className="h-3 w-3 text-trunnion-slate-500 shrink-0" />

            {/* Segment link or active label */}
            {segment.isActive ? (
              <span className="text-white font-medium truncate max-w-50">
                {segment.label}
              </span>
            ) : (
              <Link
                to={segment.path}
                className="text-trunnion-slate-400 hover:text-trunnion-gold-500 transition-colors truncate max-w-50"
              >
                {segment.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
